import { ingredientQuery } from '@/api/ingredients/ingredientQueries';
import { currentUserQuery } from '@/api/user/userQueries';
import BackButton from '@/components/common/BackButton';
import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import { Separator } from '@/components/common/Separator';
import { Spinner } from '@/components/common/Spinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardsContainer,
} from '@/components/dashboard/DashboardCard';
import { IngredientArticleRow } from '@/components/ingredients/IngredientArticleRow';
import { IngredientProperty } from '@/components/ingredients/IngredientProperty';
import { IngredientsTwoColumnSection } from '@/components/ingredients/IngredientsTwoColumnSection';
import { getStorageUrl } from '@/lib/api';
import { generateIngredientPdf } from '@/lib/utils/pdfGenerator';
import { routes } from '@/routes/routes';
import { useQuery } from '@tanstack/react-query';
import { notFound, useParams } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';

export const IngredientPage = () => {
  const { ingredientId } = useParams({
    from: '/_public-layout/ingredients/$ingredientId',
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { data: user } = useQuery({ ...currentUserQuery, retry: false });

  const {
    data: ingredient,
    isLoading,
    isError,
  } = useQuery(ingredientQuery(ingredientId));

  useEffect(() => {
    if (ingredient) {
      setIsImageLoaded(true);
    }
  }, [ingredient]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="size-10" />
      </div>
    );
  }

  if (isError || !ingredient) {
    throw notFound();
  }

  const doses = ingredient.data.content.doses
    ? ingredient.data.content.doses
        .split(/[/,]/g)
        .map((d) => d.trim())
        .filter(Boolean)
    : [];

  return (
    <main className="flex flex-col gap-12 py-12.5 lg:gap-17.5 lg:pt-22.5 lg:pb-25">
      <IngredientsTwoColumnSection>
        <div className="flex flex-col gap-5 lg:max-w-150 lg:gap-7.5">
          <BackButton to={routes.ingredients.index}>
            Back to Ingredients
          </BackButton>

          <Heading as="h1" size="h2" className="text-secondary text-left">
            {ingredient.data.name}
          </Heading>

          <p>{ingredient.data.content.description}</p>

          <div
            className={`bg-accent h-65 w-65 self-center overflow-hidden rounded-full transition-transform duration-700 ease-out md:h-70 md:w-70 lg:hidden ${
              isImageLoaded ? 'scale-100' : 'scale-0'
            }`}
          >
            <img
              src={getStorageUrl(ingredient.data.image_url)}
              alt={ingredient.data.name}
              className="h-full w-full object-cover"
            />
          </div>

          <Separator className="hidden data-[orientation=horizontal]:h-0.5 lg:block" />

          <div className="flex flex-col gap-4">
            {!!ingredient.data.content.found_in && (
              <IngredientProperty label="Found In">
                {ingredient.data.content.found_in}
              </IngredientProperty>
            )}

            {!!ingredient.data.content.form && (
              <IngredientProperty label="Form">
                {ingredient.data.content.form}
              </IngredientProperty>
            )}

            {!!ingredient.data.content.source && (
              <IngredientProperty label="Source">
                {ingredient.data.content.source}
              </IngredientProperty>
            )}

            {!!ingredient.data.content.location && (
              <IngredientProperty label="Location of Manufacturing">
                {ingredient.data.content.location}
              </IngredientProperty>
            )}

            <IngredientProperty label="Function">
              {ingredient.data.content.function}
            </IngredientProperty>

            {doses.length > 0 && (
              <IngredientProperty label="Available Doses">
                <ul className="list-inside list-disc">
                  {doses.map((dose) => (
                    <li key={dose}>{dose}</li>
                  ))}
                </ul>
              </IngredientProperty>
            )}

            {user?.data && !!ingredient.data.content.product_name && (
              <Button
                onClick={() => generateIngredientPdf(ingredient.data)}
                variant="primary"
                size="md"
                className="w-fit"
              >
                <Check className="size-5" />
                Lab tested
              </Button>
            )}
          </div>
        </div>

        <div className="hidden md:justify-self-end lg:block">
          <div
            className={`bg-accent h-40 w-40 overflow-hidden rounded-full transition-transform duration-700 ease-out md:h-auto md:w-auto lg:h-95 lg:w-95 ${
              isImageLoaded ? 'scale-100' : 'scale-0'
            }`}
          >
            <img
              src={getStorageUrl(ingredient.data.image_url)}
              alt={ingredient.data.name}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </IngredientsTwoColumnSection>

      <IngredientsTwoColumnSection className="items-center">
        <CardsContainer className="-mx-4 w-screen lg:mx-0 lg:w-full">
          <Card className="gap-4 py-7.5">
            <CardHeader className="px-7.5">
              <CardTitle>Benefits</CardTitle>
            </CardHeader>

            <CardContent className="px-7.5">
              <ul className="flex list-none flex-col gap-5">
                {ingredient.data.benefits.map((benefit) => (
                  <li key={benefit.id} className="flex items-center gap-3">
                    <span className="border-foreground size-2 shrink-0 rounded-full border" />
                    <Heading asChild size="h5" className="text-left">
                      <span>{benefit.text}</span>
                    </Heading>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </CardsContainer>

        <Heading
          asChild
          size="h3"
          className="max-w-100 lg:max-w-none lg:text-left"
        >
          <p>“{ingredient.data.content.benefits_textarea}”</p>
        </Heading>
      </IngredientsTwoColumnSection>

      <IngredientsTwoColumnSection>
        <div className="flex flex-col gap-5 lg:gap-7.5">
          <Heading
            as="h2"
            size="h2"
            className="text-secondary text-left lg:mb-3"
          >
            Evidence-Based or Bust
          </Heading>

          <p>
            We prioritise science-based supplement ingredients, guided by a
            wealth of independent research.
          </p>

          <p>
            Our experts carefully select the most impactful studies to inform
            our choices.
          </p>

          {ingredient.data.content.articles?.map((article) => (
            <Fragment key={article.source}>
              <Separator className="data-[orientation=horizontal]:h-0.5" />

              <IngredientArticleRow article={article} />
            </Fragment>
          ))}
        </div>
      </IngredientsTwoColumnSection>
    </main>
  );
};
